'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { useCart } from '@/context/CartContext';
import { useTranslation } from '@/context/LanguageContext';
import { formatPrice } from '@/lib/products';
import {
  trackCartViewed,
  trackCheckoutStarted,
  trackCheckoutButtonClicked,
  trackCheckoutRedirectedToStripe,
} from '@/lib/analytics';
import styles from './Cart.module.scss';

export default function Cart() {
  const {
    items,
    removeItem,
    updateQuantity,
    totalPrice,
    isCartOpen,
    setCartOpen,
  } = useCart();

  const { t, locale, currency } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prevCartOpen = useRef(false);

  useEffect(() => {
    if (isCartOpen && !prevCartOpen.current && items.length > 0) {
      trackCartViewed(
        items.map((i) => ({
          productId: i.productId,
          name: i.name,
          quantity: i.quantity,
          price: currency === 'eur' ? i.priceEur : i.price,
        })),
        currency
      );
    }
    prevCartOpen.current = isCartOpen;
  }, [isCartOpen]);

  const handleCheckout = async () => {
    const analyticsItems = items.map((i) => ({
      productId: i.productId,
      name: i.name,
      quantity: i.quantity,
      price: currency === 'eur' ? i.priceEur : i.price,
    }));

    trackCheckoutButtonClicked(analyticsItems, currency);
    trackCheckoutStarted(
      analyticsItems,
      currency
    );
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          locale,
          items: items.map((item) => ({
            productId: item.productId,
            size: item.size,
            stripePriceId: currency === 'eur' ? item.stripePriceIdEur : item.stripePriceId,
            quantity: item.quantity,
          })),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(t.cart.errorGeneric);
        setIsLoading(false);
        return;
      }

      if (data.url) {
        trackCheckoutRedirectedToStripe(currency, items.length);
        window.location.href = data.url;
      } else {
        setError(t.cart.errorCheckout);
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(t.cart.errorNetwork);
      setIsLoading(false);
    }
  };

  if (!isCartOpen) return null;

  return (
    <>
      <div className={styles.overlay} onClick={() => setCartOpen(false)} />
      <aside className={styles.drawer}>
        <div className={styles.header}>
          <h2>{t.cart.title}</h2>
          <button
            className={styles.close}
            onClick={() => setCartOpen(false)}
            aria-label={t.cart.close}
          >
            &times;
          </button>
        </div>

        {items.length === 0 ? (
          <div className={styles.empty}>
            <p>{t.cart.empty}</p>
            <p className={styles.emptyHint}>{t.cart.freeShippingHint}</p>
          </div>
        ) : (
          <>
            <ul className={styles.items}>
              {items.map((item) => (
                <li key={`${item.productId}-${item.size}`} className={styles.item}>
                  <div className={styles.itemImage}>
                    <Image
                      src={item.image}
                      alt={item.name}
                      fill
                      sizes="80px"
                      style={{ objectFit: 'cover' }}
                    />
                  </div>
                  <div className={styles.itemInfo}>
                    <h3>{item.name}</h3>
                    <p className={styles.itemSize}>{item.size}</p>
                    <p className={styles.itemPrice}>{formatPrice(currency === 'eur' ? item.priceEur : item.price, t)}</p>
                    <div className={styles.quantity}>
                      <button
                        onClick={() =>
                          updateQuantity(item.productId, item.size, item.quantity - 1)
                        }
                        aria-label={t.cart.reduceQuantity}
                      >
                        &minus;
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        onClick={() =>
                          updateQuantity(item.productId, item.size, item.quantity + 1)
                        }
                        aria-label={t.cart.increaseQuantity}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <button
                    className={styles.remove}
                    onClick={() => removeItem(item.productId, item.size)}
                    aria-label={t.cart.removeProduct}
                  >
                    &times;
                  </button>
                </li>
              ))}
            </ul>

            <div className={styles.footer}>
              {(() => {
                const FREE_SHIPPING_THRESHOLD = currency === 'eur' ? 8000 : 50000;
                const currentTotal = items.reduce(
                  (sum, i) => sum + (currency === 'eur' ? i.priceEur : i.price) * i.quantity,
                  0
                );
                const remaining = FREE_SHIPPING_THRESHOLD - currentTotal;
                const progress = Math.min(currentTotal / FREE_SHIPPING_THRESHOLD, 1);
                return (
                  <div className={styles.shippingBar}>
                    <div className={styles.shippingProgress}>
                      <div
                        className={styles.shippingFill}
                        style={{ width: `${progress * 100}%` }}
                      />
                    </div>
                    <p className={styles.shippingText}>
                      {remaining > 0
                        ? t.cart.shippingRemaining.replace('{amount}', formatPrice(remaining, t))
                        : t.cart.freeShipping}
                    </p>
                  </div>
                );
              })()}
              {error && (
                <div className={styles.error}>
                  <p>{error}</p>
                </div>
              )}
              <div className={styles.total}>
                <span>{t.cart.total}</span>
                <strong>{formatPrice(
                  items.reduce((sum, i) => sum + (currency === 'eur' ? i.priceEur : i.price) * i.quantity, 0),
                  t
                )}</strong>
              </div>
              <button
                className={styles.checkout}
                onClick={handleCheckout}
                disabled={isLoading}
              >
                {isLoading ? t.cart.loading : t.cart.checkout}
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
